import dayjs from "dayjs";

import { formatDateRange } from "@/utils/formatDate";
import { formatCurrency } from "@/utils/formatEuro";

import type { Auction } from "@/types/auctions";
import type { TFunction } from "i18next";

function normalizeSearchValue(value: unknown) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[,]/g, "")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getDisplayedNumberOfLots(auction: Auction) {
  const lots = auction.event_analysis?.number_of_lots ?? 0;
  return lots === 0 ? 1 : lots;
}

function getDateSearchValues(start?: string, end?: string) {
  if (!start || !end) return [];

  const startDate = dayjs.utc(start);
  const endDate = dayjs.utc(end);

  return [
    formatDateRange(start, end),
    startDate.format("MMM"),
    startDate.format("MMMM"),
    endDate.format("MMM"),
    endDate.format("MMMM"),
    startDate.format("YYYY"),
    endDate.format("YYYY"),
    startDate.format("M"),
    endDate.format("M"),
    startDate.format("D"),
    endDate.format("D"),
    startDate.format("MMM D"),
    endDate.format("MMM D"),
    startDate.format("MMM D, YYYY"),
    endDate.format("MMM D, YYYY"),
    startDate.format("MMMM YYYY"),
    endDate.format("MMMM YYYY"),
  ];
}

function getTotalValueSearchValues(value: number, currencyCode: string) {
  return [
    formatCurrency(value, currencyCode),
    formatCurrency(value, currencyCode, { notation: "standard" }),
    String(value),
    String(Math.round(value)),
  ];
}

function getAuctionSearchValues(auction: Auction, currencyCode: string, t: TFunction) {
  const totalValue = auction.event_analysis?.total_value ?? 0;

  return [
    auction.name,
    auction.place,
    auction.status,
    String(auction.num_cars),
    t("components.Auctions.cars_on_sale", { value: auction.num_cars || 0 }),
    ...getDateSearchValues(auction.start_date, auction.end_date),
    ...getTotalValueSearchValues(totalValue, currencyCode),
    String(getDisplayedNumberOfLots(auction)),
  ];
}

export function matchesSearchQuery(
  auction: Auction,
  query: string,
  currencyCode: string,
  t: TFunction
) {
  const q = normalizeSearchValue(query);
  if (!q) return true;

  return getAuctionSearchValues(auction, currencyCode, t).some((value) =>
    normalizeSearchValue(value).includes(q)
  );
}
